// src/components/dashboard/DashboardSummary.jsx
import React, { useState } from 'react'
import { faEllipsisVertical, faEye } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import DashboardCard from './DashboardCard'
import LogisticsDashboard from './LogisticsDashboard'
import useQueryWithLoader from '../../hooks/useQueryWithLoader' 
import useBaseService from '../../hooks/services/useBaseService'
import { entities } from '../../constants/entities'
import { useNavigate } from 'react-router-dom'
import { dashboardConfig } from '../../constants/dashboardConfig'
import { useApplication } from '../../hooks/useApplication'
import { useOrgan } from '../../hooks/useOrgan'
import ApplicationEnum from '../../enums/application-enum'
import useDebounce from '../../hooks/useDebounce'
import { keepPreviousData } from '@tanstack/react-query'
import { useQueryClient } from '@tanstack/react-query'
import { Menu, MenuItem, Pagination } from '@mui/material'
import { OrderSituationEnum } from '../../enums/order-enum'
import { useAuth } from '../../hooks/useAuth'
import useNotification from '../../hooks/useNotification'

const formatDateTime = (dateString) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const formatDate = (dateString) => {
  if (!dateString) return '-'
  return new Date(`${dateString}T00:00:00`).toLocaleDateString('pt-BR')
}

const formatCurrency = (amount) =>
  Number(amount ?? 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  })

const DashboardTable = ({ title, columns, rows, emptyMessage, onList, onItem, rowType = 'approvals', approvals = false, onDecision }) => (
  <section className="card shadow-sm mt-4 overflow-hidden">
    <header className="card-header bg-white py-3 d-flex flex-wrap justify-content-between align-items-center gap-2">
      <h2 className="h6 m-0 text-primary fw-bold">{title}</h2>
      <button className="btn btn-sm btn-outline-primary" onClick={onList}>Ver lista completa</button>
    </header>
    <div className="table-responsive"><table className="table table-hover align-middle mb-0">
      <thead className="table-light"><tr>{columns.map((col) => <th key={col}>{col}</th>)}<th className="text-center">Ação</th></tr></thead>
      <tbody>{rows.length === 0 ? <tr><td colSpan={columns.length + 1} className="text-center py-4 text-muted">{emptyMessage}</td></tr> : rows.map((item) => <tr key={item.id}>
        {rowType === 'rental-orders' && <><td>#{item.id}</td><td>{item.primary || '-'}</td><td>{item.secondary || '-'}</td><td><span className="badge bg-secondary">{item.tag || '-'}</span></td><td>{formatDateTime(item.created_at)}</td><td>{formatDateTime(item.deadline)}</td></>}
        {rowType === 'maintenance-warranty' && <><td>{item.equipment_number || '-'}</td><td>{item.equipment_name || '-'}</td><td>{formatDate(item.expected_return_at)}</td><td>{item.days_left ?? '-'} {item.days_left != null ? (item.days_left === 1 ? 'dia' : 'dias') : ''}</td></>}
        {rowType === 'rental-approvals' && <><td>{item.request_number || '-'}</td><td>{item.customer || '-'}</td><td>{item.organization || '-'}</td><td>{formatDateTime(item.date)}</td></>}
        {rowType === 'approvals' && <><td>{item.equipment || item.equipment_number || `#${item.id}`}</td><td>{item.description || item.primary || item.equipment_name || '-'}</td><td>{item.details || item.secondary || item.tag || '-'}</td><td>{formatDateTime(item.created_at)}</td></>}
        <td className="text-center"><div className="d-flex flex-wrap justify-content-center gap-1">{item.crossOrganization ? (item.organizationShortcut?.label ? <button className="btn btn-sm btn-outline-primary" onClick={() => onItem(item)}>Ir para {item.organizationShortcut.label}</button> : <button className="btn btn-sm btn-outline-secondary" disabled title="Organização não identificada">Organização indisponível</button>) : approvals && item.kind === 'exchange' ? <><button className="btn btn-sm btn-success" disabled={!item.service_order_id} title={!item.service_order_id ? 'Requisição não identificada' : undefined} aria-label={!item.service_order_id ? 'Aprovar indisponível: requisição não identificada' : undefined} onClick={() => onDecision(item, true)}>Aprovar</button><button className="btn btn-sm btn-outline-danger" disabled={!item.service_order_id} title={!item.service_order_id ? 'Requisição não identificada' : undefined} aria-label={!item.service_order_id ? 'Recusar indisponível: requisição não identificada' : undefined} onClick={() => onDecision(item, false)}>Recusar</button></> : <button className="btn btn-sm btn-outline-primary" onClick={() => onItem(item)}>Ver</button>}</div></td>
      </tr>)}</tbody>
    </table></div>
  </section>
)

// Menu de ações da listagem (Almoxarifado/Expedição): permite ir direto
// para o movimento ou para a requisição vinculada à linha
const RowActionsMenu = ({ item, onNavigate, onCreateMovement, isWarehouse = false }) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)

  const movementLink = item.movement_link || null
  const movementTarget = movementLink || (
    isWarehouse && item.movement_id
      ? `/aluguel/movimentos/detalhes/${item.movement_id}`
      : null
  )
  const orderLink = item.order_link || null

  const handleNavigate = (link) => {
    setAnchorEl(null)
    if (link) onNavigate(link)
  }

  return (
    <>
      <button
        className="btn btn-sm btn-info text-white"
        type="button"
        title="Ações"
        aria-label="Ações"
        onClick={(event) => setAnchorEl(event.currentTarget)}>
        <FontAwesomeIcon icon={faEllipsisVertical} />
      </button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        {isWarehouse && onCreateMovement ? (
          <MenuItem
            onClick={() => {
              setAnchorEl(null)
              onCreateMovement(item)
            }}>
            Criar Movimento
          </MenuItem>
        ) : movementTarget ? (
          <MenuItem onClick={() => handleNavigate(movementTarget)}>
            Ir para Movimento
          </MenuItem>
        ) : (
          <MenuItem disabled>Ir para Movimento</MenuItem>
        )}
        <MenuItem disabled={!orderLink} onClick={() => handleNavigate(orderLink)}>
          Ir para Requisição
        </MenuItem>
      </Menu>
    </>
  )
}

// ⬇️ 1. Adicionamos 'view' como prop, com fallback para 'padrao'
const DashboardSummary = ({ module, view = 'padrao' }) => {
  const [equipmentFilter, setEquipmentFilter] = useState('')
  const [contractSearch, setContractSearch] = useState('')
  const [contractQuery, setContractQuery] = useState('')
  const [contractPage, setContractPage] = useState(1)
  const [billingSearch, setBillingSearch] = useState('')
  const [logisticsSearch, setLogisticsSearch] = useState('')
  const [logisticsQuery, setLogisticsQuery] = useState('')
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { selectedApplication, selectApplication } = useApplication()
  const { selectedOrgan, selectOrgan } = useOrgan()
  const { user } = useAuth()
  const { showNotification } = useNotification()
  const { get: baseServiceGet, post: baseServicePost, put: baseServicePut } = useBaseService(navigate)
  
  // ⬇️ 2. Busca a config específica da view, com fallback seguro para 'padrao'
  const config = dashboardConfig[module]?.[view] || dashboardConfig[module]?.['padrao']

  const isContractView = module === 'aluguel' && view === 'licitacao'
  const isLogisticsView = module === 'aluguel' && view === 'logistica'
  // Coluna "Situação" (sem estoque / com estoque) nas listagens de
  // almoxarifado e expedição
  const showStockSituation =
    module === 'almoxarifado' || (module === 'aluguel' && view === 'expedicao')
  // Menu de ações no lugar do botão único nas visões de Almoxarifado/Expedição.
  const showRowActionsMenu = showStockSituation

  // Busca da tabela de contratos aplicada no servidor (com debounce)
  const { debouncedFn: applyContractSearch } = useDebounce((value) => {
    setContractQuery(String(value).trim())
    setContractPage(1)
  }, 400)

  // Busca da lista de requisições da logística aplicada no servidor (com debounce)
  const { debouncedFn: applyLogisticsSearch } = useDebounce((value) => {
    setLogisticsQuery(String(value).trim())
  }, 400)

  const navigateToDashboardRoute = (route) => {
    // O dashboard de Almoxarifado oferece atalhos para atender requisições
    // na área de Movimentos do módulo Aluguel. Atualiza o módulo selecionado
    // antes de navegar para que o layout e as permissões acompanhem a rota.
    if (
      module === 'almoxarifado' &&
      route?.startsWith('/aluguel/') &&
      selectedApplication?.id !== ApplicationEnum.ALUGUEL
    ) {
      selectApplication({
        id: ApplicationEnum.ALUGUEL,
        name: 'ALUGUEL',
        active: true,
      })
    }

    navigate(route)
  }

  const navigateToDashboardItem = (item) => {
    if (module === 'manutencao' && item.link) {
      navigate(item.link)
      return
    }
    if (module === 'almoxarifado') {
      const route = item.movement_id
        ? `/aluguel/movimentos/detalhes/${item.movement_id}`
        : `/aluguel/movimentos/criar?service_order_id=${item.id}`

      navigateToDashboardRoute(route)
      return
    }

    if (module === 'aluguel' && view === 'expedicao') {
      const movementId = item.movement_id || item.id
      navigateToDashboardRoute(`/aluguel/movimentos/detalhes/${movementId}`)
      return
    }

    navigateToDashboardRoute(config.actionRoute || item.link)
  }

  const createWarehouseMovement = async (item) => {
    if (!item.organization_id) {
      showNotification('warning', 'Não foi possível identificar a organização desta requisição.')
      return
    }

    try {
      const response = await baseServiceGet(
        entities.users.applicationsAndOrganizations(user.id, ApplicationEnum.ALUGUEL)
      )
      const organizations = response.result?.data || []
      const organization = organizations.find(
        (entry) => String(entry.id) === String(item.organization_id)
      )

      if (!organization) {
        showNotification('warning', 'Você não tem autorização para acessar a organização desta requisição.')
        return
      }

      selectOrgan(organization)
      selectApplication({ id: ApplicationEnum.ALUGUEL, name: 'ALUGUEL', active: true })
      navigate(`/aluguel/movimentos/criar?service_order_id=${item.id}`)
    } catch {
      // useBaseService exibe a notificação padrão para falhas da API.
    }
  }

  const goToOrganizationItem = async (item, applicationId) => {
    // Only use a complete organization returned by the user's authorized-applications endpoint.
    try {
      const response = await baseServiceGet(
        entities.users.applicationsAndOrganizations(user.id, applicationId)
      )
      const organizations = response.result?.data || []
      const organization = organizations.find(
        (entry) => String(entry.id) === String(item.organization_id)
      )
      if (!organization) return
      selectOrgan(organization)
      if (selectedApplication?.id !== applicationId) {
        const appsResponse = await baseServiceGet(entities.applications.get)
        const application = (appsResponse.result?.data || []).find((entry) => String(entry.id) === String(applicationId))
        if (application) selectApplication(application)
      }
      navigate(applicationId === ApplicationEnum.ALUGUEL ? '/dashboard/aluguel?view=gestor' : '/dashboard/manutencao?view=gestor')
    } catch {}
  }

  // ⬇️ 3. Monta a URL com o parâmetro view se não for 'padrao'
  const { data: dashboardData, isLoading, isFetching } = useQueryWithLoader({
    queryKey: [
      'dashboard',
      selectedOrgan?.id,
      module,
      view,
      module === 'aluguel' && view === 'direcao' ? equipmentFilter : null,
      isContractView ? contractPage : null,
      isContractView ? contractQuery : null,
      isLogisticsView ? logisticsQuery : null,
    ],
    queryFn: async () => {
      const response = await baseServiceGet(entities.dashboard.get(module), {
        ...(view && view !== 'padrao' ? { view } : {}), // ⬅️ view vai como param
        ...(module === 'aluguel' && view === 'direcao' && equipmentFilter ? { equipment: equipmentFilter } : {}),
        ...(isContractView
          ? {
              page: contractPage,
              ...(contractQuery ? { search: contractQuery } : {}),
            }
          : {}),
        ...(isLogisticsView && logisticsQuery
          ? { search: logisticsQuery }
          : {}),
      })
      return response.result
    },
    staleTime: 60 * 1000,
    enabled: !!module,
    // A busca da logística usa indicador inline; evita o overlay global a cada tecla.
    showGlobalLoader: !isLogisticsView,
    // Mantém a página atual visível enquanto a próxima é carregada
    ...(isContractView || isLogisticsView ? { placeholderData: keepPreviousData } : {}),
  })

  if (!config) return null
  if (isLoading || !dashboardData) return <div className="text-center py-5 text-muted">Carregando dashboard...</div>

  const { cards, tables = {}, pagination } = dashboardData

  if (isLogisticsView) {
    return (
      <LogisticsDashboard
        cards={cards || {}}
        requests={tables.logistics_requests ?? []}
        search={logisticsSearch}
        onSearchChange={(value) => {
          setLogisticsSearch(value)
          applyLogisticsSearch(value)
        }}
        onClearSearch={() => {
          setLogisticsSearch('')
          setLogisticsQuery('')
          applyLogisticsSearch('')
        }}
        isFetching={isFetching}
        onOpenRequest={(request) =>
          navigateToDashboardRoute(
            request.link || `/aluguel/requisicoes/${request.id}/detalhes`
          )
        }
        onViewAll={() => navigateToDashboardRoute('/aluguel/requisicoes')}
      />
    )
  }

  // ⬇️ 5. Usa a tableKey dinâmica definida na config, em vez de hardcodar 'pending_orders'
  const urgentItems = tables[config.tableKey] ?? []
  const filteredBillings = module === 'aluguel' && view === 'financeiro'
    ? urgentItems.filter((billing) =>
        String(billing.number ?? '')
          .toLocaleLowerCase('pt-BR')
          .includes(billingSearch.trim().toLocaleLowerCase('pt-BR'))
      )
    : []

  if (module === 'aluguel' && view === 'direcao') {
    const approvalRows = tables.administrative_approvals || []
    return (
      <>
        <div className="row">
          {config.cards.map((cardMeta) => <DashboardCard key={cardMeta.key} title={cardMeta.title} value={cards[cardMeta.key] ?? 0} icon={cardMeta.icon} color={cardMeta.color} colSize="col-xl-6" />)}
        </div>
        <div className="card shadow-sm mt-4">
          <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center"><h6 className="m-0 font-weight-bold text-primary">Aprovações pendentes</h6><input className="form-control form-control-sm" style={{ maxWidth: 260 }} placeholder="Filtrar por tombo/equipamento" value={equipmentFilter} onChange={(event) => setEquipmentFilter(event.target.value)} /></div>
          <div className="table-responsive"><table className="table table-hover align-middle mb-0"><thead className="table-light"><tr><th>Equipamento</th><th>Tipo de aprovação</th><th>Detalhes</th><th>Solicitado em</th><th>Ação</th></tr></thead><tbody>
            {approvalRows.length === 0 ? <tr><td colSpan="5" className="text-center py-4 text-muted">Nenhuma aprovação pendente.</td></tr> : approvalRows.map((item) => <tr key={item.id}><td>{item.equipment}</td><td>{item.description}</td><td>{item.details || '-'}</td><td>{formatDateTime(item.created_at)}</td><td className="text-nowrap"><button className="btn btn-sm btn-outline-primary" onClick={() => navigate('/aluguel/requisicoes')}>Ver requisições</button></td></tr>)}
          </tbody></table></div>
        </div>
      </>
    )
  }

  if (view === 'gestor' && (module === 'aluguel' || module === 'manutencao')) {
    const approvalKeys = ['administrative_approvals']
    return <>
      <div className="row">
        {config.cards.map((cardMeta) => <DashboardCard key={cardMeta.key} title={cardMeta.title} value={cards[cardMeta.key] ?? 0} icon={cardMeta.icon} color={cardMeta.color} colSize={`col-xl-${Math.min(4, Math.floor(12 / config.cards.length))}`} />)}
      </div>
      {approvalKeys.map((key) => <DashboardTable key={key} title={module === 'aluguel' ? 'Aprovações pendentes' : config.title} columns={module === 'aluguel' ? ['Nº Requisição', 'Cliente', 'Organização', 'Data'] : config.columns} rowType={module === 'aluguel' ? 'rental-approvals' : 'approvals'} rows={(tables[key] || []).map((item) => ({ ...item, organizationShortcut: { label: item.organization_alias || item.organization || item.organization_name }, crossOrganization: Boolean(item.organization_id) && String(item.organization_id) !== String(selectedOrgan?.id) }))} emptyMessage={config.emptyMessage} approvals={module === 'aluguel'} onDecision={async (item, approved) => {
        if (!window.confirm(`Confirma ${approved ? 'aprovar' : 'recusar'} este excedente?`)) return
        try {
          if (module === 'aluguel') {
            await baseServicePut(
              entities.serviceOrders.updateStatus(item.service_order_id),
              {
                order: {
                  situation: approved
                    ? OrderSituationEnum.APPROVED
                    : OrderSituationEnum.CANCELLED,
                },
              }
            )
            await Promise.all([
              queryClient.invalidateQueries({
                queryKey: ['dashboard', selectedOrgan?.id, module, view],
              }),
              queryClient.invalidateQueries({ queryKey: ['orders'] }),
              queryClient.invalidateQueries({ queryKey: ['contractOsItemsEquipments'] }),
            ])
          } else {
            await baseServicePost(`/service-order-items/${item.item_id}/exchange-approval`, { approved }, { skipNotification: true })
            await queryClient.invalidateQueries({
              queryKey: ['dashboard', selectedOrgan?.id, module, view],
            })
          }
        } catch {
          // useBaseService reports API errors through the standard notification hook.
        }
      }} onList={() => navigateToDashboardRoute(module === 'aluguel' ? '/aluguel/requisicoes' : '/manutencao')} onItem={(item) => {
        if (item.crossOrganization && item.organization_id && item.organizationShortcut?.label) return goToOrganizationItem(item, module === 'aluguel' ? ApplicationEnum.ALUGUEL : ApplicationEnum.MANUTENÇÃO)
        if (item.link) navigate(item.link)
        else navigate(`/manutencao/detalhes/${item.maintenance_id || item.item_id}`)
      }} />)}
    </>
  }

  if (module === 'manutencao') {
    return (
      <>
        <div className="row">
          {config.cards.map((cardMeta) => (
            <DashboardCard
              key={cardMeta.key}
              title={cardMeta.title}
              value={cards[cardMeta.key] ?? 0}
              icon={cardMeta.icon}
              color={cardMeta.color}
              colSize="col-xl-6"
            />
          ))}
        </div>

        <div className="card shadow-sm mt-4">
          <div className="card-header bg-white py-3">
            <h6 className="m-0 font-weight-bold text-primary">{config.title}</h6>
          </div>
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  {config.columns.map((column) => <th key={column}>{column}</th>)}
                  <th className="text-center">Ação</th>
                </tr>
              </thead>
              <tbody>
                {urgentItems.length === 0 ? (
                  <tr>
                    <td colSpan={config.columns.length + 1} className="text-center py-4 text-muted">
                      {config.emptyMessage}
                    </td>
                  </tr>
                ) : urgentItems.map((item) => (
                  <tr key={item.id}>
                    <td>{item.equipment_number}</td>
                    <td>{item.equipment_name}</td>
                    <td>{formatDate(item.expected_return_at)}</td>
                    <td className={item.days_left <= 7 ? 'text-danger fw-bold' : 'fw-bold'}>
                      {item.days_left} {item.days_left === 1 ? 'dia' : 'dias'}
                    </td>
                    <td className="text-center">
                      <button
                        className="btn btn-sm btn-info text-white"
                        title="Ver manutenção"
                        onClick={() => navigate(item.link)}
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </>
    )
  }

  if (module === 'aluguel' && view === 'licitacao') {
    return (
      <>
        <div className="row">
          {config.cards.map((cardMeta) => (
            <DashboardCard
              key={cardMeta.key}
              title={cardMeta.title}
              value={cards[cardMeta.key] ?? 0}
              icon={cardMeta.icon}
              color={cardMeta.color}
              colSize={`col-xl-${Math.floor(12 / config.cards.length)}`}
            />
          ))}
        </div>

        <div className="card shadow-sm mt-4">
          <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center gap-3">
            <h6 className="m-0 font-weight-bold text-primary">{config.title}</h6>
            <input
              type="search"
              className="form-control form-control-sm"
              style={{ maxWidth: 320 }}
              placeholder="Buscar por nome ou número do contrato"
              aria-label="Buscar contratos por nome ou número"
              value={contractSearch}
              onChange={(event) => {
                setContractSearch(event.target.value)
                applyContractSearch(event.target.value)
              }}
            />
          </div>
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  {config.columns.map((column) => <th key={column}>{column}</th>)}
                  <th className="text-center">Ação</th>
                </tr>
              </thead>
              <tbody>
                {urgentItems.length === 0 ? (
                  <tr>
                    <td colSpan={config.columns.length + 1} className="text-center py-4 text-muted">
                      {contractQuery ? 'Nenhum contrato corresponde à busca.' : config.emptyMessage}
                    </td>
                  </tr>
                ) : urgentItems.map((contract) => (
                  <tr key={contract.id}>
                    <td>
                      <strong>{contract.number || '-'}</strong>
                      {contract.name && <div className="text-muted small">{contract.name}</div>}
                    </td>
                    <td>{contract.customer || '-'}</td>
                    <td>{contract.status || '-'}</td>
                    <td className="text-center">
                      <button
                        className="btn btn-sm btn-info text-white"
                        title="Ver detalhes do contrato"
                        onClick={() => navigateToDashboardRoute(contract.link)}
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 px-3 py-2 border-top">
            <span className="text-muted small">
              {pagination
                ? `${pagination.total} contrato(s) • página ${pagination.current_page} de ${pagination.last_page}`
                : ''}
            </span>
            {pagination && pagination.last_page > 1 && (
              <Pagination
                count={pagination.last_page}
                page={pagination.current_page}
                onChange={(event, page) => setContractPage(page)}
                color="primary"
                size="small"
                showFirstButton
                showLastButton
                siblingCount={1}
                boundaryCount={1}
              />
            )}
          </div>
        </div>
      </>
    )
  }

  if (module === 'aluguel' && view === 'financeiro') {
    return (
      <>
        <div className="row">
          {config.cards.map((cardMeta) => (
            <DashboardCard
              key={cardMeta.key}
              title={cardMeta.title}
              value={cards[cardMeta.key] ?? 0}
              icon={cardMeta.icon}
              color={cardMeta.color}
              colSize={`col-xl-${Math.floor(12 / config.cards.length)}`}
            />
          ))}
        </div>

        <div className="card shadow-sm mt-4">
          <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center gap-3">
            <h6 className="m-0 font-weight-bold text-primary">{config.title}</h6>
            <input
              type="search"
              className="form-control form-control-sm"
              style={{ maxWidth: 320 }}
              placeholder="Filtrar pelo número do faturamento"
              aria-label="Filtrar pelo número do faturamento"
              value={billingSearch}
              onChange={(event) => setBillingSearch(event.target.value)}
            />
          </div>
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  {config.columns.map((column) => <th key={column}>{column}</th>)}
                  <th className="text-center">Ação</th>
                </tr>
              </thead>
              <tbody>
                {filteredBillings.length === 0 ? (
                  <tr>
                    <td colSpan={config.columns.length + 1} className="text-center py-4 text-muted">
                      {billingSearch.trim() ? 'Nenhum faturamento corresponde à busca.' : config.emptyMessage}
                    </td>
                  </tr>
                ) : filteredBillings.map((billing) => (
                  <tr key={billing.id}>
                    <td><strong>{billing.number || '-'}</strong></td>
                    <td>{billing.customer || '-'}</td>
                    <td>{formatCurrency(billing.amount)}</td>
                    <td>
                      <span className={`badge ${billing.is_approved ? 'bg-success' : 'bg-secondary'}`}>
                        {billing.is_approved ? 'Sim' : 'Não'}
                      </span>
                    </td>
                    <td className="text-center">
                      <button
                        className="btn btn-sm btn-info text-white"
                        title="Ver detalhes do faturamento"
                        onClick={() => navigateToDashboardRoute(billing.link)}
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <div className="row">
        {config.cards.map((cardMeta) => (
          <DashboardCard
            key={cardMeta.key}
            title={cardMeta.title}
            value={cards[cardMeta.key] ?? 0}
            icon={cardMeta.icon}
            color={cardMeta.color}
            colSize={`col-xl-${Math.floor(12 / config.cards.length)}`}
          />
        ))}
      </div>

      <div className="row mt-4">
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
              {/* O título agora vem dinamicamente da config da view */}
              <h6 className="m-0 font-weight-bold text-primary">
                {config.title}
              </h6>
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={() => navigateToDashboardRoute(config.listRoute || `/${module}`)}
              >
                Ver Todas
              </button>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      {config.columns.map((col) => (
                        <th key={col}>{col}</th>
                      ))}
                      <th className="text-center">Ação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {urgentItems.length === 0 ? (
                      <tr>
                        <td colSpan={config.columns.length + 1} className="text-center py-4 text-muted">
                          {config.emptyMessage || 'Nenhum registro encontrado.'}
                        </td>
                      </tr>
                    ) : (
                      urgentItems.map((item) => (
                        <tr key={item.id}>
                          <td><strong>#{item.id}</strong></td>
                          <td>{item.primary}</td>
                          <td>{item.secondary}</td>
                          {module === 'almoxarifado' && <td>{item.organization_name || '-'}</td>}
                          <td>
                            <span className="badge bg-secondary">{item.tag}</span>
                          </td>
                          <td className="text-danger fw-bold">
                            {formatDateTime(item.created_at)}
                          </td>
                          <td>{formatDateTime(item.deadline)}</td>
                          {showStockSituation && (
                            <td>
                              <span
                                className={`badge ${
                                  item.has_awaiting_purchase_items
                                    ? 'bg-danger'
                                    : 'bg-success'
                                }`}>
                                {item.has_awaiting_purchase_items
                                  ? 'Sem estoque'
                                  : 'Com estoque'}
                              </span>
                            </td>
                          )}
                          <td className="text-center">
                            {showRowActionsMenu ? (
                              <RowActionsMenu
                                item={item}
                                onNavigate={navigateToDashboardRoute}
                onCreateMovement={module === 'almoxarifado' ? createWarehouseMovement : undefined}
                                isWarehouse={module === 'almoxarifado'}
                              />
                            ) : (
                              <button
                                className="btn btn-sm btn-info text-white"
                                title="Ver Detalhes"
                                onClick={() => navigateToDashboardItem(item)}
                              >
                                <FontAwesomeIcon icon={faEye} />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default DashboardSummary
